import { z } from 'zod'

/**
 * Dados institucionais da paroquia.
 *
 * Ficam no banco (tabela Setting, chave `parish`) e nao no codigo porque a
 * secretaria precisa poder corrigir telefone, endereco, logo e horario de
 * atendimento sem esperar deploy. Alimentam o rodape, a pagina de contato, o
 * cabecalho, o favicon e o fallback das meta tags.
 *
 * Mora no pacote compartilhado para o formulario do painel validar com o mesmo
 * schema que a API aplica na gravacao.
 */

/** URL opcional: aceita vazio, porque o formulario envia string vazia. */
const urlOpcional = z.union([z.url('Informe uma URL valida'), z.literal('')]).default('')

export const parishSettingsSchema = z.object({
  name: z.string().min(1).default('Paroquia de Pacaembu'),
  /** Versao curta, usada em titulo de pagina, cabecalho e card social. */
  shortName: z.string().min(1).default('Paroquia de Pacaembu'),
  description: z
    .string()
    .min(1)
    .default('Noticias, horarios de missa, eventos e prestacao de contas da Paroquia de Pacaembu.'),
  address: z.string().default(''),
  city: z.string().default('Pacaembu'),
  state: z.string().default('SP'),
  phone: z.string().default(''),
  whatsapp: z.string().default(''),
  email: z.string().default(''),
  officeHours: z.string().default(''),
  instagramUrl: urlOpcional,
  facebookUrl: urlOpcional,
  youtubeUrl: urlOpcional,

  /**
   * Logo da paroquia.
   *
   * Serve ao cabecalho do portal, ao painel e TAMBEM ao favicon - as variantes
   * quadradas do icone sao geradas quando a logo e definida. Nulo usa o brasao
   * padrao embutido na aplicacao.
   */
  logoMediaId: z.string().nullable().default(null),

  /** Imagem padrao do card social (1200x630). */
  socialImageUrl: z.string().nullable().default(null),
})

export type ParishSettings = z.infer<typeof parishSettingsSchema>

/**
 * Corpo aceito pelo formulario do painel.
 *
 * Separado do schema de leitura: na escrita, nome e descricao sao obrigatorios
 * com mensagem em pt-BR, e os campos de contato ganham limite de tamanho.
 */
export const parishSettingsInputSchema = z.object({
  name: z.string().min(3, 'Informe o nome da paroquia').max(160).trim(),
  shortName: z.string().min(2, 'Informe o nome curto').max(80).trim(),
  description: z
    .string()
    .min(10, 'Escreva uma descricao')
    .max(320, 'No maximo 320 caracteres - ela vai no card compartilhado')
    .trim(),
  address: z.string().max(300).trim().default(''),
  city: z.string().max(120).trim().default(''),
  state: z.string().max(2, 'Use a sigla do estado').trim().default(''),
  phone: z.string().max(40).trim().default(''),
  whatsapp: z.string().max(40).trim().default(''),
  email: z.union([z.email('Informe um e-mail valido'), z.literal('')]).default(''),
  officeHours: z.string().max(300).trim().default(''),
  instagramUrl: urlOpcional,
  facebookUrl: urlOpcional,
  youtubeUrl: urlOpcional,
  logoMediaId: z.string().nullable().default(null),
})

export type ParishSettingsInput = z.infer<typeof parishSettingsInputSchema>

/** Tamanhos quadrados gerados a partir da logo, para favicon e atalho. */
export const TAMANHOS_DE_ICONE = [32, 180, 512] as const

/** Nome da variante no registro de midia, por tamanho. */
export function chaveDaVarianteDeIcone(tamanho: number): string {
  return `icone${tamanho}`
}
